import React from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider } from './context/AuthProvider';
import { TenantProvider } from './context/TenantProvider';
import { PermissionsProvider } from './context/PermissionsProvider';
import { AccessProvider } from './context/AccessProvider';
import { WalletProvider } from './context/WalletProvider';
import { useAuth } from './hooks/useAuth';
import { ErrorBoundary } from './components/ErrorBoundary';
import { FloatingAssistant } from './components/assistant/FloatingAssistant';
import { Login } from './pages/Login';
import { Home } from './pages/Home';
import { Buildings } from './pages/Buildings';
import { Services } from './pages/Services';
import { DataExchange } from './pages/DataExchange';
import { Wiki } from './pages/Wiki';
import { Settings } from './pages/Settings';
import { Profile } from './pages/Profile';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return (
    <>
      {children}
      <FloatingAssistant />
    </>
  );
};

export const AppRoutes = () => (
  <Routes>
    <Route path="/login" element={<Login />} />
    {/* Home keeps the /dashboard path: Keycloak sign-in redirects and bookmarks point there. */}
    <Route
      path="/dashboard"
      element={(
        <ProtectedRoute>
          <Home />
        </ProtectedRoute>
      )}
    />
    <Route
      path="/buildings"
      element={(
        <ProtectedRoute>
          <Buildings />
        </ProtectedRoute>
      )}
    />
    <Route
      path="/services"
      element={(
        <ProtectedRoute>
          <Services />
        </ProtectedRoute>
      )}
    />
    <Route
      path="/data-exchange"
      element={(
        <ProtectedRoute>
          <DataExchange />
        </ProtectedRoute>
      )}
    />
    {/* Analytics was merged into Buildings; keep old links working. */}
    <Route path="/analytics" element={<Navigate to="/buildings" replace />} />
    <Route
      path="/wiki"
      element={(
        <ProtectedRoute>
          <Wiki />
        </ProtectedRoute>
      )}
    />
    <Route
      path="/settings"
      element={(
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      )}
    />
    <Route
      path="/profile"
      element={(
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      )}
    />
    <Route path="/" element={<Navigate to="/dashboard" replace />} />
  </Routes>
);

function App() {
  return (
    <Router>
      <AuthProvider>
        <TenantProvider>
          <PermissionsProvider>
            <AccessProvider>
              <WalletProvider>
                <ErrorBoundary>
                  <AppRoutes />
                  <ToastContainer position="bottom-right" autoClose={3500} />
                </ErrorBoundary>
              </WalletProvider>
            </AccessProvider>
          </PermissionsProvider>
        </TenantProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;

